import { capitalize } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { MarketGroup } from '../../@types/api';
import { QUERY_KEYS } from '../../constants';
import { createEditColumn, createIsActiveColumn, createSportIconColumn } from '../../helpers/columns';
import { defaultColumnsMarketMix } from '../../helpers/table';
import useMutateData from '../../hooks/useMutateData';
import { usePagination } from '../../hooks/usePagination';
import useSort from '../../hooks/useSort';
import { createColumn, formatSnakeCaseString } from '../../utils';
import MarketGroupsPositionInput from '../atoms/MarketGroupsPositionInput';
import TableTemplate from '../templates/TableTemplate';
import { MARKET_GROUP_TYPE } from '../../constants/enums';

const MarketMixPage = () => {
  const {
    data: marketMixData,
    updateQueryParams,
    isFetching,
    isLoading,
    changeQuery,
  } = usePagination<MarketGroup>('market-type-groups', {
    page: 1,
    limit: 25,
    type: MARKET_GROUP_TYPE.MIXED,
  });

  const { deleteData, updateData } = useMutateData('market-type-groups', [QUERY_KEYS.marketTypeGroups]);
  const { handleSort } = useSort(changeQuery);

  const columns: GridColDef[] = useMemo(
    () => [
      createColumn('id', 'ID', { sortable: false }),
      createSportIconColumn(),
      createColumn('sportName', 'Sport', {
        valueGetter: (value, row, column, apiRef) => row.sport.name,
      }),
      createColumn('name', 'Name'),
      createColumn('position', 'Position', {
        renderCell: (params) => (
          <MarketGroupsPositionInput
            defaultValue={params.row.position}
            onEnterClick={updateData}
            marketId={params.id as string}
          />
        ),
      }),
      createColumn('usedFor', 'Used for', {
        valueGetter: (value, row, column, apiRef) => formatSnakeCaseString(row.usedFor),
      }),
      createIsActiveColumn({
        updateData,
      }),
      createColumn('displayType', 'Display', {
        valueGetter: (value, row, column, apiRef) => capitalize(row.displayType),
      }),
      createEditColumn({
        deleteData,
        columnName: 'Actions',
      }),
    ],
    [deleteData, updateData]
  );

  const handleSearch = useCallback(
    (value: string | null) => {
      changeQuery({ search: value });
    },
    [changeQuery]
  );

  return (
    <TableTemplate
      rows={marketMixData?.items || []}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={marketMixData?.count || 0}
      createItemBtnLabel='Add new market mix'
      defaultVisibleColumns={defaultColumnsMarketMix}
      handlePaginationModelChange={updateQueryParams}
      handleSearch={handleSearch}
      handleSort={handleSort}
      changeQuery={changeQuery}
      formType='market-mix'
    />
  );
};

export default MarketMixPage;
